import { useMemo } from 'react';

import path from 'path';

import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';

import ErrorPage from '~components/errorBoundary/errorPage';
import ErrorType from '~components/errorBoundary/errorType';
import { pagePathSegments, queryParameters } from '~constants';
import PageLayout from '~layouts/page/pageLayout';
import { getService } from '~services/serviceFactory';

import ScheduledActionDetailsContainer from '../containers/scheduledActionDetailsContainer';
import ScheduledActionsService from '../services/scheduledActionsService';

interface ScheduledActionNavigationState {
	device?: {
		id?: string;
		name?: string;
	};
	organisation?: {
		id?: string;
		name?: string;
	};
}

const scheduledActionService = getService(ScheduledActionsService);
const ScheduledActionDetailPage = () => {
	const { t } = useTranslation();
	const { scheduledActionId } = useParams();
	const [searchParams] = useSearchParams();
	const organisationId = searchParams.get(queryParameters.OverwrittenClientId) ?? undefined;
	const state = (useLocation().state as ScheduledActionNavigationState | null) ?? null;

	const {
		data: scheduledActionData,
		error: scheduledActionError,
		isLoading: scheduledActionIsLoading,
	} = useSWR(
		scheduledActionService.getScheduledActionByIdCacheKey(scheduledActionId, organisationId),
		([_, id, orgId]) => {
			return id && orgId ? scheduledActionService.getScheduledActionById(id, orgId) : null;
		}
	);

	const computedTitle = useMemo(
		() =>
			scheduledActionData?.name ??
			`${t('scheduledAction.labels.single', { ns: 'entities' })} ${scheduledActionId ?? ''}`,
		[scheduledActionData, scheduledActionId, t]
	);

	const crumbs = useMemo(
		() => [
			...(state?.device ?
				[
					{
						title: t('device.labels.plural', { ns: 'entities' }),
						path: path.join('/', pagePathSegments.Devices),
					},
					{
						title: state.device.name ?? '',
						path: path.join('/', pagePathSegments.Devices, state.device.id ?? ''),
					},
				]
			:	[]),
			...(state?.organisation ?
				[
					{
						title: t('organisation.labels.plural', { ns: 'entities' }),
						path: path.join('/', pagePathSegments.Organisations),
					},
					{
						title: state.organisation.name ?? '',
						path: path.join('/', pagePathSegments.Organisations, state.organisation.id ?? ''),
					},
				]
			:	[]),
			{
				title: computedTitle,
				path: '',
			},
		],
		[computedTitle, state, t]
	);

	// --- All hooks above, all early returns below ---

	if (scheduledActionId === undefined || organisationId === undefined) {
		return <Navigate to={pagePathSegments.Home} replace />;
	}

	if (!scheduledActionIsLoading && scheduledActionError) {
		return <ErrorPage type={ErrorType.NotFound} />;
	}

	return (
		<PageLayout
			isLoading={scheduledActionIsLoading}
			pageTitle={computedTitle}
			headerInfo={{
				title: computedTitle,
			}}
			crumbs={crumbs}
		>
			<ScheduledActionDetailsContainer
				scheduledActionId={scheduledActionId}
				organisationId={organisationId}
			/>
		</PageLayout>
	);
};

export default ScheduledActionDetailPage;
